/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// Thin wrappers that hit the Supabase edge functions which mint Stripe URLs.
// We deliberately don't call Stripe directly from the browser with secret data —
// Checkout sessions and Billing Portal URLs must be created server-side.
//
// SECURITY: there is deliberately no Stripe Payment Link fallback here. A
// payment link is a client-side URL with a client-supplied plan parameter, so
// anyone could edit it in devtools and buy a tier on the wrong terms. Every
// purchase goes through `stripe-checkout`, which resolves the price itself from
// a validated lookup_key and ignores anything else the browser sends.

import { supabase } from "./supabaseClient.js";
import { getMyPrimaryAccount } from "./accountService.js";

async function callEdge(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    console.error(`[stripeLinks] ${name} failed:`, error);
    // The function returns deliberately generic, non-leaky messages (e.g. the
    // seat-band mismatch). Surface that when we can; otherwise stay vague.
    let message = "Could not contact billing service.";
    try {
      const body = await error.context?.json?.();
      if (typeof body?.error === "string" && body.error.length < 300) message = body.error;
    } catch { /* keep the generic message */ }
    throw new Error(message);
  }
  return data;
}

/**
 * Start Checkout for a published price.
 *
 * `lookupKey` is a catalog key like "growth_6to10_monthly" — never a Stripe
 * price ID and never an amount. The edge function re-resolves it against
 * Stripe and rejects anything that isn't an active price on one of our tier
 * products, so tampering with it here achieves nothing beyond picking a
 * different published plan.
 */
export async function startCheckout({ lookupKey } = {}) {
  if (!lookupKey) throw new Error("No plan selected.");
  // Name the workspace being bought for. The seat-band check runs against this
  // account's headcount, and the function used to GUESS which one — so a user in
  // two workspaces could have their purchase sized against the other one's team.
  // The function verifies membership and refuses an id we shouldn't be sending,
  // so this is a correctness signal, not something it trusts.
  const acct = await getMyPrimaryAccount().catch(() => null);
  const data = await callEdge("stripe-checkout", {
    lookup_key: lookupKey,
    ...(acct?.accountId ? { accountId: acct.accountId } : {}),
  });
  if (!data?.url) throw new Error("Checkout did not return a URL.");
  window.location.assign(data.url);
}

export async function openBillingPortal() {
  const data = await callEdge("stripe-portal", {});
  if (!data?.url) throw new Error("Portal did not return a URL.");
  window.location.assign(data.url);
}
